import { Matrix4, Quaternion, Vector3 } from 'three';

export const WORLD_UP = new Vector3(0, 1, 0);
export const LOCAL_FORWARD = new Vector3(0, 0, -1);
export const LOCAL_RIGHT = new Vector3(1, 0, 0);
export const LOCAL_UP = new Vector3(0, 1, 0);

const lookMatrix = new Matrix4();
const lookOrigin = new Vector3();

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function lerp(current: number, target: number, alpha: number): number {
  return current + (target - current) * clamp(alpha, 0, 1);
}

export function expDamp(current: number, target: number, lambda: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-lambda * dt));
}

export function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export function randomSign(): number {
  return Math.random() < 0.5 ? -1 : 1;
}

export function forwardOf(quaternion: Quaternion, target = new Vector3()): Vector3 {
  return target.copy(LOCAL_FORWARD).applyQuaternion(quaternion).normalize();
}

export function rightOf(quaternion: Quaternion, target = new Vector3()): Vector3 {
  return target.copy(LOCAL_RIGHT).applyQuaternion(quaternion).normalize();
}

export function upOf(quaternion: Quaternion, target = new Vector3()): Vector3 {
  return target.copy(LOCAL_UP).applyQuaternion(quaternion).normalize();
}

export function lookRotation(forward: Vector3, upHint = WORLD_UP, target = new Quaternion()): Quaternion {
  const safeForward = forward.lengthSq() > 0.000001
    ? forward.clone().normalize()
    : LOCAL_FORWARD.clone();
  lookMatrix.lookAt(lookOrigin, safeForward, upHint);
  return target.setFromRotationMatrix(lookMatrix).normalize();
}

export function rotateTowards(
  current: Quaternion,
  desiredForward: Vector3,
  maxRadians: number,
  target = new Quaternion(),
): Quaternion {
  const desired = lookRotation(desiredForward, WORLD_UP, target);
  current.rotateTowards(desired, maxRadians);
  return current.normalize();
}

export function horizontalDistance(a: Vector3, b: Vector3): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.hypot(dx, dz);
}

export function formatMeters(value: number): string {
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return `${Math.round(value)}`;
}
